import { useEffect, useState } from 'react';
import { Globe2, Image, Layers3, Pause, Play, Save } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { projectAPI } from '@/services/api';
import TypedContractBuilder from '@/components/campaign/TypedContractBuilder';
import {
  TailAdminEmptyState,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from '@/components/template/tailadmin/DashboardPrimitives';

const emptyDraft = {
  name: '', description: '', shortDescription: '', logoUrl: '', coverUrl: '',
  marketingUrl: '', category: '', defaultGeography: '',
};

const ClientProjects = () => {
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [activeEnvironment, setActiveEnvironment] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await projectAPI.getProjects();
      const rows = response.data || [];
      setProjects(rows);
      if (!selectedId && rows[0]) setSelectedId(rows[0].project_id || rows[0].projectId);
    } catch {
      toast.error('Failed to load Projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const project = projects.find((item) => (item.project_id || item.projectId) === selectedId);
    if (!project) return;
    setDraft({
      name: project.name || '',
      description: project.description || '',
      shortDescription: project.short_description || '',
      logoUrl: project.logo_url || '',
      coverUrl: project.cover_url || '',
      marketingUrl: project.marketing_url || '',
      category: project.category || '',
      defaultGeography: project.default_geography || '',
    });
  }, [projects, selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setContracts([]);
      setActiveEnvironment(null);
      return;
    }
    projectAPI.listContracts(selectedId)
      .then((response) => {
        setContracts(response.data?.contracts || []);
        setActiveEnvironment(response.data?.active_environment || null);
      })
      .catch(() => {
        setContracts([]);
        setActiveEnvironment(null);
      });
  }, [selectedId]);

  const selected = projects.find((item) => (item.project_id || item.projectId) === selectedId);
  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await projectAPI.updateProject(selectedId, draft);
      toast.success('Project identity saved');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Project');
    } finally {
      setSaving(false);
    }
  };

  const transition = async (status) => {
    setSaving(true);
    try {
      await projectAPI.transitionProject(selectedId, status);
      toast.success(`Project moved to ${status}`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Project transition failed');
    } finally {
      setSaving(false);
    }
  };

  const saveContract = async (data) => {
    setSaving(true);
    try {
      await projectAPI.saveContract(selectedId, data);
      const response = await projectAPI.listContracts(selectedId);
      setContracts(response.data?.contracts || []);
      toast.success('Reusable Project contract saved as a draft revision');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Project contract');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 p-4 md:p-6">
      <TailAdminPageHeader
        eyebrow="Brand workspace"
        title="Projects"
        description="Project identity is shared by its Offers, Proove Requests and reusable integrations. Commercial terms remain campaign-specific."
      />
      {loading ? <p className="text-sm text-gray-500">Loading Projects…</p> : projects.length === 0 ? (
        <TailAdminEmptyState icon={Layers3} title="No Projects yet" description="Create your first Project from the New Offer flow." />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
          <TailAdminPanel title="Your Projects" action={null}>
            <div className="space-y-2">
              {projects.map((project) => {
                const id = project.project_id || project.projectId;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSelectedId(id)}
                    className={`w-full rounded-xl border p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-200 ${selectedId === id ? 'border-brand-400 bg-brand-50' : 'border-gray-200'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-900">{project.name}</span>
                      <TailAdminStatusBadge tone={project.status === 'published' || project.status === 'active' ? 'success' : 'neutral'}>{project.status}</TailAdminStatusBadge>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-gray-500">{project.short_description || project.description || 'Identity not completed'}</p>
                  </button>
                );
              })}
            </div>
          </TailAdminPanel>

          {selected && (
            <div className="space-y-5">
            <TailAdminPanel title="Project identity" description="Publish only after these public identity fields are accurate." action={<Globe2 className="h-5 w-5 text-brand-500" />}>
              <div className="mb-4 flex flex-wrap gap-2 rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600">
                <span className="inline-flex items-center gap-2">
                  Official domain
                  <TailAdminStatusBadge tone={selected.domain_verification_status === 'verified' ? 'success' : 'warning'}>
                    {selected.domain_verification_status || 'unverified'}
                  </TailAdminStatusBadge>
                </span>
                <span className="inline-flex items-center gap-2">
                  Contact email
                  <TailAdminStatusBadge tone={selected.email_verification_status === 'verified' ? 'success' : 'warning'}>
                    {selected.email_verification_status || 'unverified'}
                  </TailAdminStatusBadge>
                </span>
                <span className="basis-full text-gray-500">Verification state is Project-owned and is shared by every Offer and Proove Request under this brand.</span>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {[
                  ['Project name', 'name', 'text', 'Your public brand or initiative'],
                  ['Short description', 'shortDescription', 'text', 'A concise marketplace summary'],
                  ['Category', 'category', 'text', 'e.g. Fintech'],
                  ['Default geography', 'defaultGeography', 'text', 'e.g. Nigeria'],
                  ['Marketing URL', 'marketingUrl', 'url', 'https://example.com'],
                  ['Logo URL', 'logoUrl', 'url', 'https://…'],
                  ['Cover image URL', 'coverUrl', 'url', 'https://…'],
                ].map(([label, key, type, placeholder]) => (
                  <label key={key} className="text-sm font-medium text-gray-700">
                    {label}
                    <Input type={type} value={draft[key]} onChange={(event) => update(key, event.target.value)} placeholder={placeholder} className="mt-1" />
                  </label>
                ))}
                <label className="text-sm font-medium text-gray-700 md:col-span-2">
                  Full description
                  <textarea value={draft.description} onChange={(event) => update('description', event.target.value)} className="mt-1 min-h-28 w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
                </label>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
                <Button onClick={save} disabled={saving}><Save className="mr-2 h-4 w-4" />Save identity</Button>
                {['ready', 'active', 'paused'].includes(selected.status) && (
                  <Button variant="outline" onClick={() => transition('published')} disabled={saving}><Play className="mr-2 h-4 w-4" />Publish Project</Button>
                )}
                {selected.status === 'published' && (
                  <Button variant="outline" onClick={() => transition('paused')} disabled={saving}><Pause className="mr-2 h-4 w-4" />Pause</Button>
                )}
                <span className="ml-auto inline-flex items-center gap-2 text-xs text-gray-500"><Image className="h-4 w-4" />Assets must use HTTPS and tenant-owned storage paths.</span>
              </div>
            </TailAdminPanel>
            <TailAdminPanel title="Reusable integration contracts" description="Save credentials and mappings once at Project level. Verify and bind an immutable revision from a draft Proove Request." action={<Layers3 className="h-5 w-5 text-brand-500" />}>
              <TypedContractBuilder
                contracts={contracts}
                requiredPurposes={[]}
                activeEnvironment={activeEnvironment}
                onSave={saveContract}
                saving={saving}
                showReadiness={false}
              />
            </TailAdminPanel>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientProjects;
